import { isEmpty, sortBy } from 'lodash';
import { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { AdvertiserCampaignProvider, useFeesBySolutionContext } from 'sonata-platform-components';
import { Input, Selector, SelectorOptionPropsInterface } from 'sonata-react-components';
import { normalizeCountryToCca3 } from 'sonata-static-storage/lib/helpers/countries.helpers';
import { normalizeWithUnit } from 'sonata-static-storage/lib/helpers/i18nUnits.helpers';
import { checkUserResourcePermissions } from 'sonata-static-storage/lib/helpers/user.helpers';
import { countriesDictionary } from 'sonata-static-storage/lib/samples/countries.samples';
import { creativeFeeDictionary } from 'sonata-static-storage/lib/samples/creativeFees.samples';
import { iabCategoriesDictionary } from 'sonata-static-storage/lib/samples/iabCategories.samples';
import { worldCountries } from 'sonata-static-storage/lib/samples/worldCountries.samples';

import { AdvertiserCampaignSelector } from '@/components/common/AdvertiserCampaignSelector';
import { AttributionModel } from './AttributionModel';
import { BillingSubsidiary } from './BillingSubsidiary';
import { CampaignAdditionalDetails } from './CampaignAdditionalDetails/CampaignAdditionalDetails';
import CampaignStrategy from './CampaingStrategy/CampaignStrategy';
import { StepFourStyled } from './stepFour.styled';

import type { SelectedMediaInfo } from '@/components/common/AdvertiserCampaignSelector/types';

type State = {
  showEngagementFeeInput: boolean;
};

function StepFour() {
  const { formatMessage } = useIntl();
  const [{ showEngagementFeeInput }, setState] = useState<State>({
    showEngagementFeeInput: false
  });

  const {
    campaign,
    contextualThirdPartyProviderVariables,
    currencyInfo,
    getStep4,
    handleAudienceExportUserIdsChange,
    handleCampaignAutoSmartChange,
    handleCampaignTemplateChange,
    handleContextualDataProviderChange,
    handleForecastCampaignInventoryChange,
    handleForecastCampaignLocationChange,
    handleForecastCampaignOnlyChange,
    handleForecastCampaignTargetUsersValidatorsChange,
    handleMediaInfoSelectedChange,
    handlePredicioBillPacingConfigChange,
    handleStep4Change,
    handleTemplateMailingChange,
    handleTestCampaignChange,
    handleTestPreProdCampaignChange,
    handleTrueReachConfigurationChange,
    user,
    userRoles
  } = useFeesBySolutionContext();

  const {
    businessSolution,
    campaignAudienceExportUserIdsEnabled,
    campaignAutoSmart,
    campaignForecastByInventoryItem,
    campaignForecastByLocation,
    campaignForecastOnly,
    campaignForecastTargetUsersValidator,
    campaignIsTemplate,
    campaignMailingType,
    campaignPreProdTest,
    campaignSolutionsFeesEnabled,
    campaignTeselaEnabled,
    campaignTest,
    campaignTrueReachEnabled,
    channel,
    countries,
    creativeFee,
    iabCategories,
    ioNumber,
    mainCampaignKPI,
    mediaAdvertiser,
    mediaInfoSelected,
    name
  } = getStep4();

  const getBidMargin = (mediaType: 'openRtb' | 'pmp') => {
    let margin;
    if (user?.bidMargin?.[mediaType]) {
      margin = user.bidMargin[mediaType];
    } else if (countriesDictionary[user.country]) {
      margin = countriesDictionary[user.country].bidMargin;
    } else {
      margin = countriesDictionary.default.bidMargin;
    }
    return margin;
  };

  const setNewCampaignProperties = () => {
    const campaignMargin: Record<string, any> = {};
    if (campaign.mediaType !== 'hybrid') {
      campaignMargin[campaign.mediaType] = getBidMargin(campaign.mediaType);
    } else {
      campaignMargin.openRtb = getBidMargin('openRtb');
      campaignMargin.pmp = getBidMargin('pmp');
    }

    handleStep4Change({
      // @ts-ignore
      agencyFeeModel: user.agencyFeeModel,
      margin: campaignMargin,
      // @ts-ignore
      markupIncluded: user.markupIncluded,
      // @ts-ignore
      platformFeeIncluded: user.platformFeeIncluded
    });
  };

  const getPredicioValue = () => {
    if (campaign.targeting?.audience?.billPacing?.predicio) {
      return campaign.targeting.audience.billPacing.predicio;
    }
    const predicioUserConfig = user.audienceConfiguration.billPacingProviders.find(
      (provider: { provider: string; value: number }) => {
        // @ts-ignore
        return provider === 'predicio';
      }
    ) as { value: number } | undefined;
    let predicioBillPacingValue = predicioUserConfig ? predicioUserConfig.value : 0;
    if (
      campaign.targeting.audience &&
      campaign.targeting.audience.billPacing &&
      'predicio' in campaign.targeting.audience.billPacing
    ) {
      predicioBillPacingValue = campaign.targeting.audience.billPacing.predicio;
    }
    return predicioBillPacingValue;
  };

  const setCampaignProperties = () => {
    const { mailingType } = campaign.template || {
      isTemplate: false
    };
    handleStep4Change({
      noNeededStaticReports: !!campaign.forecast?.only
    });
    handleTemplateMailingChange(mailingType || 'none');
    handlePredicioBillPacingConfigChange(getPredicioValue());
    if (!campaign._id) {
      setNewCampaignProperties();
    }
    if (!campaign.countries?.length) {
      handleStep4Change({
        countries: [normalizeCountryToCca3(user.country)]
      });
    }
  };

  useEffect(() => {
    setCampaignProperties();
  }, []);

  const countryOptions: SelectorOptionPropsInterface[] = sortBy(
    worldCountries.map(worldCountry => ({
      id: worldCountry.cca3.toLowerCase(),
      label: worldCountry.name.common
    })),
    'label'
  );

  const countryValuesOptions: SelectorOptionPropsInterface[] = countries?.map((val: string) => ({
    id: val,
    label: worldCountries.find(country => country.cca3.toLowerCase() === val)?.name.common
  }));

  const campaignCategoryOptions: SelectorOptionPropsInterface[] = iabCategoriesDictionary.reduce(
    (acc: SelectorOptionPropsInterface[], iabCategory) => {
      return [
        ...acc,
        {
          id: iabCategory.categoryId,
          label: iabCategory.categoryName
        }
      ];
    },
    [
      {
        id: 'noCategory',
        label: formatMessage({
          defaultMessage: 'No Category',
          id: 'feesBySolutionWizard.stepFour.noCategory'
        })
      }
    ]
  );

  const engagementFeeOptions: SelectorOptionPropsInterface[] = (() => {
    const creativeFeeOps = [];
    const creativeFees = user.country === 'mx' ? creativeFeeDictionary.mx : creativeFeeDictionary.default;
    creativeFees.forEach(cf => {
      creativeFeeOps.push({
        id: cf,
        label: normalizeWithUnit(cf, currencyInfo)
      });
    });
    creativeFeeOps.push({
      id: 'otherCreativeFee',
      label: formatMessage({
        defaultMessage: 'Other',
        id: 'feesBySolutionWizard.stepFour.otherCreativeFee'
      })
    });
    return creativeFeeOps;
  })();

  const defaultEngagementFeeValue = () => {
    const creativeFees = user.country === 'mx' ? creativeFeeDictionary.mx : creativeFeeDictionary.default;
    if (creativeFee == null) {
      return [{ id: creativeFees[0], label: normalizeWithUnit(creativeFees[0], currencyInfo) }];
    }
    const creativeFeeFound = creativeFees.find(fee => fee === creativeFee);
    if (creativeFeeFound != null) {
      return [{ id: creativeFeeFound, label: normalizeWithUnit(creativeFeeFound, currencyInfo) }];
    }
    return [
      {
        id: 'otherCreativeFee',
        label: formatMessage({
          defaultMessage: 'Other',
          id: 'feesBySolutionWizard.stepFour.otherCreativeFee'
        })
      }
    ];
  };

  const handleCountryChange = (options: SelectorOptionPropsInterface[]) => {
    handleStep4Change({
      countries: options.map(option => option.id)
    });
  };

  const handleCampaignNameChange = (text: string | FileList | null) => {
    handleStep4Change({
      name: text
    });
  };

  const handleCampaignCategoryChange = (option: SelectorOptionPropsInterface) => {
    handleStep4Change({
      iabCategories: option.id === 'noCategory' ? [] : [option.id]
    });
  };

  const handleIoNumberChange = (text: string | FileList | null) => {
    handleStep4Change({
      ioNumber: (text as string)?.trim()
    });
  };

  const handleMediaAdvertiserChange = (mediaAdvertiserId: string | null) => {
    handleStep4Change({
      mediaAdvertiser: mediaAdvertiserId
    });
  };

  const handleMediaInfoChange = (mediaInfo: SelectedMediaInfo) => {
    handleMediaInfoSelectedChange({
      mediaInfoSelected: mediaInfo
    });
  };

  const handleEngagementFeeChange = (option: SelectorOptionPropsInterface) => {
    if (option.id === 'otherCreativeFee') {
      handleStep4Change({ creativeFee: null });
      setState(prevState => ({
        ...prevState,
        showEngagementFeeInput: true
      }));
    } else {
      handleStep4Change({
        creativeFee: parseInt(option.value!)
      });
      setState(prevState => ({
        ...prevState,
        showEngagementFeeInput: false
      }));
    }
  };

  const handleCreativeFeeChange = (fee: string | FileList | null) => {
    handleStep4Change({
      creativeFee: fee ? parseInt(fee as string) : 0
    });
  };

  const handleChannelChange = (option: string) => {
    handleStep4Change({
      channel: option
    });
  };

  const handleKpiChange = (option: string) => {
    handleStep4Change({
      mainCampaignKPI: option
    });
  };

  const handleBusinessSolutionChange = (option: string) => {
    handleStep4Change({
      businessSolution: option
    });
  };

  const getCampaignCategoryName = (categoryId: string) => {
    const category = iabCategoriesDictionary.find(iabCategory => iabCategory.categoryId === categoryId)!;
    return category.categoryName;
  };

  const checkCampaignAdditionalDetailsPermissions = () => {
    return [
      'trueReach',
      'autoSmart',
      'campaignTemplate',
      'campaignTesting',
      'campaignPreProdTesting',
      'campaignForecastOnly',
      'campaignForecastByLocation',
      'campaignForecastByInventoryItem'
    ].some(permission => checkUserResourcePermissions(permission, user, userRoles));
  };
  const attributionModelPermissions = checkUserResourcePermissions('attributionModel', user, userRoles);

  return (
    <StepFourStyled className="feesBySolutionStepFour" data-testid="feesBySolutionStepFour">
      <h2 className="feesBySolutionStepFour__title">
        {formatMessage({
          defaultMessage: 'Main settings',
          id: 'feesBySolutionWizard.stepFour.title.mainSettings'
        })}
      </h2>
      <div className="feesBySolutionStepFour__row">
        <div className="feesBySolutionStepFour__80">
          <Input
            id="feesBySolutionStepFour-campaignName"
            label={formatMessage({
              defaultMessage: 'Name',
              id: 'feesBySolutionWizard.stepFour.name.label'
            })}
            name="stepFour-campaignName"
            onChange={handleCampaignNameChange}
            placeholder={formatMessage({
              defaultMessage: 'Write a name...',
              id: 'feesBySolutionWizard.stepFour.name.placeholder'
            })}
            required
            shape="squareUI"
            type="text"
            value={name}
          />
        </div>
        <div className="feesBySolutionStepFour__20">
          <Input
            disabled={ioNumber}
            id={'feesBySolutionStepFour-ioNumber'}
            label={formatMessage({
              defaultMessage: 'IO Number',
              id: 'feesBySolutionWizard.stepFour.campaignIONumber.label'
            })}
            name="stepFour-campaignIoNumber"
            onChange={handleIoNumberChange}
            placeholder={formatMessage({
              defaultMessage: '1232',
              id: 'feesBySolutionWizard.stepFour.campaignIONumber.placeholder'
            })}
            required
            shape="squareUI"
            type="text"
            value={ioNumber}
          />
        </div>
      </div>
      <div className="feesBySolutionStepFour__row">
        <Selector
          className="feesBySolutionStepFour__15"
          defaultValue={countryValuesOptions}
          isDisabled={campaign.insertionOrder}
          label={formatMessage({
            defaultMessage: 'Country',
            id: 'feesBySolutionWizard.stepFour.country'
          })}
          onChange={handleCountryChange}
          options={countryOptions}
          required
          type="multiple"
          value={countryValuesOptions}
        />
        <Selector
          isDisabled={campaign.insertionOrder}
          label={formatMessage({
            defaultMessage: 'Category',
            id: 'feesBySolutionWizard.stepFour.campaignCategory'
          })}
          onChange={handleCampaignCategoryChange}
          options={campaignCategoryOptions}
          required
          type="simple"
          value={
            isEmpty(iabCategories)
              ? [
                  {
                    id: 'noCategory',
                    label: formatMessage({
                      defaultMessage: 'No Category',
                      id: 'feesBySolutionWizard.stepFour.noCategory'
                    })
                  }
                ]
              : [
                  {
                    id: iabCategories[0],
                    label: getCampaignCategoryName(iabCategories[0])
                  }
                ]
          }
        />
      </div>
      <div className="feesBySolutionStepFour__row">
        <AdvertiserCampaignProvider
          campaignMediaAdvertiser={mediaAdvertiser}
          mediaInfoSelected={mediaInfoSelected}
          mode="notEditable"
          onMediaAdvertiserChange={handleMediaAdvertiserChange}
          onMediaInfoSelectedChange={handleMediaInfoChange}
          user={user}
          userRoles={userRoles}
        >
          <AdvertiserCampaignSelector />
        </AdvertiserCampaignProvider>
      </div>
      <div>
        <h2 className="feesBySolutionStepFour__titleStrategy">
          {formatMessage({
            defaultMessage: 'Campaign Strategy',
            id: 'feesBySolutionWizard.stepFour.title'
          })}
        </h2>
        <div className="feesBySolutionStepFour__row">
          {checkUserResourcePermissions('creativeFee', user, userRoles) && (
            <>
              <Selector
                label={formatMessage({
                  defaultMessage: 'Engagement Fee',
                  id: 'feesBySolutionWizard.stepFour.engagementFee'
                })}
                onChange={handleEngagementFeeChange}
                options={engagementFeeOptions}
                type="simple"
                value={defaultEngagementFeeValue()}
              />
              {showEngagementFeeInput && (
                <Input
                  currencyConfig={currencyInfo}
                  id="campaignDetails__creativeFeeInput"
                  name="stepFour-budget"
                  numberFormat="currency"
                  onChange={handleCreativeFeeChange}
                  shape="squareUI"
                  type="number"
                  value={creativeFee || 0.0}
                />
              )}
            </>
          )}
        </div>
        {attributionModelPermissions && <AttributionModel />}
        <div className="feesBySolutionStepFour__row">
          {checkUserResourcePermissions('strategyInfo', user, userRoles) && (
            <CampaignStrategy
              campaignBusinessSolution={businessSolution}
              campaignChannel={channel}
              campaignMainCampaignKPI={mainCampaignKPI}
              onBusinessSolutionChange={handleBusinessSolutionChange}
              onChannelChange={handleChannelChange}
              onKpiChange={handleKpiChange}
            />
          )}
        </div>
        <div className="feesBySolutionStepFour__row">
          <div className="feesBySolutionStepFour__half">
            <BillingSubsidiary />
          </div>
        </div>
        <div className="feesBySolutionStepFour__row">
          {checkCampaignAdditionalDetailsPermissions() && (
            <CampaignAdditionalDetails
              campaignAudienceExportUserIdsEnabled={campaignAudienceExportUserIdsEnabled}
              campaignAutoSmart={campaignAutoSmart}
              campaignForecastByInventoryItem={campaignForecastByInventoryItem}
              campaignForecastByLocation={campaignForecastByLocation}
              campaignForecastOnly={campaignForecastOnly}
              campaignForecastTargetUsersValidator={campaignForecastTargetUsersValidator}
              campaignIsTemplate={campaignIsTemplate}
              campaignMailingType={campaignMailingType || 'none'}
              campaignPreProdTest={campaignPreProdTest}
              campaignPredicioBillPacingValue={getPredicioValue()}
              campaignSolutionsFeesEnabled={!!campaignSolutionsFeesEnabled}
              campaignTeselaEnabled={campaignTeselaEnabled}
              campaignTest={campaignTest}
              campaignTrueReachEnabled={campaignTrueReachEnabled}
              contextualDataProviders={campaign.contextualDataProviders}
              contextualThirdPartyProviderVariables={contextualThirdPartyProviderVariables}
              onAudienceExportUserIdsChange={handleAudienceExportUserIdsChange}
              onCampaignAutoSmartChange={handleCampaignAutoSmartChange}
              onCampaignTemplateCampaignChange={handleCampaignTemplateChange}
              onContextualDataProviderChange={handleContextualDataProviderChange}
              onForecastCampaignInventoryChange={handleForecastCampaignInventoryChange}
              onForecastCampaignLocationChange={handleForecastCampaignLocationChange}
              onForecastCampaignOnlyChange={handleForecastCampaignOnlyChange}
              onForecastCampaignTargetUsersValidatorsChange={handleForecastCampaignTargetUsersValidatorsChange}
              onPredicioBillPacingConfigChange={handlePredicioBillPacingConfigChange}
              onTemplateMailingChange={handleTemplateMailingChange}
              onTestCampaignChange={handleTestCampaignChange}
              onTestPreProdCampaignChange={handleTestPreProdCampaignChange}
              onTrueReachConfigurationChange={handleTrueReachConfigurationChange}
              user={user}
              userRoles={userRoles}
            />
          )}
        </div>
      </div>
    </StepFourStyled>
  );
}

export default StepFour;
